import { ThemedText } from '@/components/themed-text';
import type { SegmentPage } from '@/content/seo/segments';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Text, View } from 'react-native';
import type { IoniconName } from './seo-blocks';
import { useThemedHex } from '@/hooks/use-theme-colors';

/**
 * Prints do app desenhados em código (página de agendamento, lembrete no
 * WhatsApp, painel de cobrança). Decorativos: dados de exemplo, largura fixa
 * e texto de uma linha só — nada quebra dentro do mock.
 */

export type MockData = SegmentPage['mock'];

const WA_HEADER = '#075E54';
const WA_WALLPAPER = '#ECE5DD';
const WA_BUBBLE_OUT = '#DCF8C6';
const WA_TICK = '#34B7F1';
const tabular = { fontVariant: ['tabular-nums' as const] };

export function PhoneFrame({
  children,
  size = 'md',
}: {
  children: React.ReactNode;
  size?: 'md' | 'sm';
}) {
  return (
    <View
      aria-hidden
      className={`rounded-[40px] bg-ink p-2 shadow-2xl ${size === 'md' ? 'w-[300px]' : 'w-[260px]'}`}
    >
      <View className={`overflow-hidden rounded-[32px] bg-card ${size === 'md' ? 'h-[560px]' : 'h-[490px]'}`}>
        <View className="absolute left-1/2 top-2.5 z-10 h-6 w-20 -translate-x-1/2 rounded-full bg-ink" />
        {children}
      </View>
    </View>
  );
}

function Line({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <ThemedText numberOfLines={1} className={className}>
      {children}
    </ThemedText>
  );
}

/** Página pública de agendamento do prestador. */
export function BookingMock({ mock }: { mock: MockData }) {
  const slots = ['09:40', '10:20', '11:00', '14:00', '14:40', '16:20'];
  const days = [
    ['seg', '6'],
    ['ter', '7'],
    ['qua', '8'],
    ['qui', '9'],
    ['sex', '10'],
  ];
  return (
    <View className="px-3.5 pb-4 pt-11">
      <View className="flex-row items-center gap-3">
        <View className="h-12 w-12 items-center justify-center rounded-full bg-ink">
          <Ionicons name={mock.icon as IoniconName} size={22} color="#fff" />
        </View>
        <View className="flex-1">
          <Line className="font-semibold text-sm">{mock.business}</Line>
          <Line className="text-xs text-neutral-500">{mock.place}</Line>
        </View>
      </View>

      <Line className="mb-2 mt-4 font-semibold text-xs">Serviço</Line>
      {mock.services.map((service, i) => (
        <View
          key={service.name}
          className={`mb-2 flex-row items-center justify-between gap-2 rounded-xl border px-3 py-3 ${
            i === 0 ? 'border-primary bg-primary-50' : 'border-neutral-200'
          }`}
        >
          <Line className="flex-1 font-semibold text-xs">{service.name}</Line>
          <Text className="font-semibold text-xs text-neutral-900" style={tabular}>
            {service.price}
          </Text>
        </View>
      ))}

      <Line className="mb-2 mt-2 font-semibold text-xs">Outubro</Line>
      <View className="flex-row gap-1">
        {days.map(([d, n]) => (
          <View
            key={n}
            className={`flex-1 items-center rounded-xl py-2 ${n === '7' ? 'bg-primary' : 'bg-neutral-100'}`}
          >
            <Text className={`text-xs ${n === '7' ? 'text-white' : 'text-neutral-500'}`}>{d}</Text>
            <Text className={`font-bold text-sm ${n === '7' ? 'text-white' : 'text-neutral-900'}`}>{n}</Text>
          </View>
        ))}
      </View>

      <Line className="mb-2 mt-4 font-semibold text-xs">Horários livres</Line>
      <View className="flex-row flex-wrap gap-2">
        {slots.map((slot) => (
          <View
            key={slot}
            className={`h-8 w-[30%] grow items-center justify-center rounded-full ${
              slot === '10:20' ? 'bg-primary' : 'border border-neutral-200'
            }`}
          >
            <Text className={`text-xs ${slot === '10:20' ? 'text-white' : 'text-neutral-700'}`} style={tabular}>
              {slot}
            </Text>
          </View>
        ))}
      </View>

      <View className="mt-4 h-11 items-center justify-center rounded-full bg-primary">
        <Text className="font-semibold text-sm text-white">Agendar 10:20</Text>
      </View>
    </View>
  );
}

function Bubble({ out, time, children }: { out?: boolean; time: string; children: React.ReactNode }) {
  return (
    <View
      className={`max-w-[88%] rounded-2xl px-3 py-2 ${out ? 'self-end rounded-tr-sm' : 'self-start rounded-tl-sm bg-card'}`}
      style={out ? { backgroundColor: WA_BUBBLE_OUT } : undefined}
    >
      {children}
      <View className="mt-1 flex-row items-center justify-end gap-1">
        <Text className="text-xs text-neutral-400">{time}</Text>
        {out ? <Ionicons name="checkmark-done" size={13} color={WA_TICK} /> : null}
      </View>
    </View>
  );
}

/** "R$ 45" vira "R$ 45,00", como a mensagem de cobrança escreve. */
const chargeValue = (mock: MockData) => {
  if (mock.charge) return mock.charge;
  const price = mock.services[0]?.price ?? '';
  return /^R\$ [\d.]+$/.test(price) ? `${price},00` : price;
};

/**
 * O que o cliente recebe no WhatsApp (planos pagos), como o app manda: o
 * lembrete do horário é só texto (`schedule-cron.service.ts`) e a cobrança
 * vem em outra mensagem, com valor, vencimento e link.
 */
export function ChatMock({ mock, inPhone = true }: { mock: MockData; inPhone?: boolean }) {
  const service = mock.services[0]?.name ?? '';
  const showAppointment = mock.appointmentReminder !== false;
  return (
    <View className="flex-1">
      <View
        className={`flex-row items-center gap-3 px-4 pb-3 ${inPhone ? 'pt-11' : 'pt-3'}`}
        style={{ backgroundColor: WA_HEADER }}
      >
        <View className="h-9 w-9 items-center justify-center rounded-full bg-white/20">
          <Ionicons name="person" size={18} color="#fff" />
        </View>
        <View className="flex-1">
          <Text numberOfLines={1} className="font-semibold text-sm text-white">
            {mock.business}
          </Text>
        </View>
      </View>
      <View className="flex-1 gap-2 px-3 py-4" style={{ backgroundColor: WA_WALLPAPER }}>
        {showAppointment ? (
          <>
            <DayChip label="06/10" />
            <Bubble out time={mock.reminderAt ?? '10:20'}>
              <Text className="text-sm text-neutral-800">
                Lembrete: você tem {service} agendado para 07/10 às 10:20.
              </Text>
              {mock.reminderExtra ? (
                <Text numberOfLines={1} className="mt-1 text-sm text-neutral-800">
                  {mock.reminderExtra}
                </Text>
              ) : null}
            </Bubble>
          </>
        ) : null}
        <DayChip label="07/10" />
        <Bubble out time="08:00">
          <Text numberOfLines={2} className="text-sm text-neutral-800">
            Lembrete da sua cobrança: {service}
          </Text>
          <Text numberOfLines={1} className="mt-1 text-sm text-neutral-800" style={tabular}>
            Valor: {chargeValue(mock)}
          </Text>
          <Text numberOfLines={1} className="text-sm text-neutral-800" style={tabular}>
            Vencimento: 07/10 (hoje)
          </Text>
          <View className="mt-2 flex-row items-center gap-1.5 rounded-xl bg-white/70 px-3 py-2">
            <Ionicons name="link" size={14} color="#CC3C00" />
            <Text numberOfLines={1} className="font-semibold text-sm text-primary-700">
              Link para pagar
            </Text>
          </View>
        </Bubble>
      </View>
    </View>
  );
}

function DayChip({ label }: { label: string }) {
  return (
    <View className="self-center rounded-lg bg-white/80 px-2.5 py-1">
      <Text className="text-xs text-neutral-500" style={tabular}>
        {label}
      </Text>
    </View>
  );
}

function ChargeRow({ paid, who, value }: { paid: boolean; who: string; value: string }) {
  const th = useThemedHex();
  return (
    <View
      className={`mb-3 flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 p-3.5 ${
        paid ? 'bg-success-50' : 'bg-warning-50'
      }`}
    >
      <Ionicons name={paid ? 'checkmark-circle' : 'time-outline'} size={22} color={paid ? '#16A34A' : th('#B45309')} />
      <View className="min-w-0 flex-1">
        <Line className="font-bold text-sm text-neutral-900">{paid ? 'Pago' : 'Vence amanhã'}</Line>
        <Line className={`text-xs ${paid ? 'text-success-600' : 'text-warning-600'}`}>{who}</Line>
      </View>
      <Text className={`font-bold text-sm ${paid ? 'text-success-600' : 'text-neutral-900'}`} style={tabular}>
        {value}
      </Text>
    </View>
  );
}

/** Painel de cobranças do prestador, como o `SimulatedApp` da landing. */
export function ChargesMock({ mock }: { mock: MockData }) {
  const value = chargeValue(mock);
  const second = mock.charge ?? mock.services[1]?.price ?? value;
  return (
    <View className="px-3.5 pb-4 pt-11">
      <View className="mb-4 flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <View className="h-9 w-9 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="wallet-outline" size={18} />
          </View>
          <Text className="font-bold text-sm text-neutral-900">Outubro</Text>
        </View>
      </View>
      <ChargeRow paid who="Rafael" value={value} />
      <ChargeRow paid who="Tiago" value={second} />
      <ChargeRow paid={false} who="Bruno" value={value} />
      <View className="mb-2 mt-2 flex-row justify-between">
        <Text className="text-xs text-neutral-500">Recebido no mês</Text>
        <Text className="font-bold text-xs text-primary" style={tabular}>
          62%
        </Text>
      </View>
      <View className="h-2.5 overflow-hidden rounded-full bg-neutral-100">
        <View className="h-full w-[62%] rounded-full bg-primary" />
      </View>
    </View>
  );
}

/**
 * Palco do hero: agendamento no meio, WhatsApp e cobrança dos lados (só no
 * desktop). Ramo sem agenda para o cliente (`kind: 'monthly'`): cobrança no
 * meio e o WhatsApp ao lado.
 */
export function PhoneStage({ mock }: { mock: MockData }) {
  if (mock.kind === 'monthly') {
    return (
      <View className="h-[400px] flex-row items-start justify-center gap-6 overflow-hidden border-b border-neutral-900/5 md:h-[480px]">
        <View className="mt-16 hidden md:flex">
          <PhoneFrame size="sm">
            <ChatMock mock={mock} />
          </PhoneFrame>
        </View>
        <PhoneFrame>
          <ChargesMock mock={mock} />
        </PhoneFrame>
      </View>
    );
  }
  return (
    <View className="h-[400px] flex-row items-start justify-center gap-6 overflow-hidden border-b border-neutral-900/5 md:h-[480px]">
      <View className="mt-16 hidden md:flex">
        <PhoneFrame size="sm">
          <ChatMock mock={mock} />
        </PhoneFrame>
      </View>
      <PhoneFrame>
        <BookingMock mock={mock} />
      </PhoneFrame>
      <View className="mt-16 hidden md:flex">
        <PhoneFrame size="sm">
          <ChargesMock mock={mock} />
        </PhoneFrame>
      </View>
    </View>
  );
}
